import React from "react";
import {connect} from "react-redux";
import {Alert, Button, Col, Collapse, Descriptions, Form, Input, message, Modal, Row, Select} from "antd";
import {displayErrors} from "../../libs/utils";
import {generateContentWriterArticle} from "../../actions/content_writer";

class GenerateArticleModal extends React.Component {
    state = {
        loading: false,
        description: '',
        keywords: null,
        additional_info: null,
        showExampleRequestsModal: false,
        article_length: '5_7',
        tone_of_voice: 'CASUAL',
        point_of_view: 'FIRST_PERSON',
        content_type: 'BLOG_POST',
    }

    closeModal(requestId = null) {
        this.setState({
            loading: false,
            description: '',
            keywords: null,
            additional_info: null,
            showExampleRequestsModal: false,
            article_length: '5_7',
            tone_of_voice: 'CASUAL',
            point_of_view: 'FIRST_PERSON',
            content_type: 'BLOG_POST',
        })

        this.props.close(requestId);
    }

    generate() {
        if(this.state.description.trim() === '') {
            return message.error('Please enter a website description!');
        }

        let data = {
            'description': this.state.description.trim(),
            'article_length': this.state.article_length,
            'tone_of_voice': this.state.tone_of_voice,
            'point_of_view': this.state.point_of_view,
            'content_type': this.state.content_type,
        }

        if(this.state.keywords !== null && this.state.keywords.trim().length > 0) {
            data['keywords'] = this.state.keywords.trim().split(',').map(keyword => keyword.trim());
        }

        if(this.state.additional_info!== null && this.state.additional_info.trim().length > 0) {
            data['additional_info'] = this.state.additional_info.trim();
        }

        this.setState({ loading: true });

        this.props.generateContentWriterArticle(this.props.projectId, this.props.articleId, data, (res) => {
            message.success('Generation request sent successfully!');
            this.closeModal(res.data.request_id);
        }, (err) => {
            if(typeof err.response !== 'undefined') {
                this.setState({ loading: false });
                displayErrors(err.response.data);
            }
        })
    }

    render() {
        const { TextArea } = Input;
        const { Panel } = Collapse;
        const { Option } = Select;

        return <>
            <Modal
                centered
                title='Generate Article'
                open={this.props.showModal}
                width={600}
                destroyOnClose={true}
                footer={<Row justify='space-between'>
                    <Col>
                        <Button onClick={() => this.closeModal()} disabled={this.state.loading}>Close</Button>
                    </Col>
                    <Col>
                        <Button type='primary' onClick={() => this.generate()} disabled={this.state.loading} loading={this.state.loading}>Generate</Button>
                    </Col>
                </Row>}
                onCancel={() => this.closeModal()}>
                <Alert showIcon type='warning' message='Note: Your existing article will be replaced with the newly generated version.' />
                <br />
                <p>
                    Briefly describe what the article should be about. Include key details (topic/subject, angle/thesis, target audience,
                    key points) or goals so we can create content that meets your expectations. <Button type='link' style={{padding: 0}} onClick={() => this.setState({ showExampleRequestsModal: true })}>Click here to view examples</Button>
                </p>

                <Descriptions bordered size='small' layout='horizontal' column={1}>
                    <Descriptions.Item label='Article length' labelStyle={{width: '50%'}}>
                        <Select value={this.state.article_length} onChange={(value) => this.setState({ article_length: value })} style={{width: '100%'}}>
                            <Option value='2_3'>Shortest (2-3 sections)</Option>
                            <Option value='3_5'>Short (3-5 sections)</Option>
                            <Option value='5_7'>Medium (5-7 sections)</Option>
                            <Option value='7_10'>Long (7-10 sections)</Option>
                            <Option value='10_12'>Longest (10-12 sections)</Option>
                        </Select>
                    </Descriptions.Item>
                    <Descriptions.Item label='Tone of voice'>
                        <Select value={this.state.tone_of_voice} onChange={(value) => this.setState({ tone_of_voice: value })} style={{width: '100%'}}>
                            <Option value='EXCITED'>Excited</Option>
                            <Option value='PROFESSIONAL'>Professional</Option>
                            <Option value='FRIENDLY'>Friendly</Option>
                            <Option value='FORMAL'>Formal</Option>
                            <Option value='CASUAL'>Casual</Option>
                            <Option value='HUMOROUS'>Humorous</Option>
                        </Select>
                    </Descriptions.Item>
                    <Descriptions.Item label='Point of view'>
                        <Select value={this.state.point_of_view} onChange={(value) => this.setState({ point_of_view: value })} style={{width: '100%'}}>
                            <Option value='FIRST_PERSON'>First person (I, me, my, mine)</Option>
                            <Option value='FIRST_PERSON_PLURAL'>First person plural (we, us, our, ours)</Option>
                            <Option value='SECOND_PERSON'>Second person (you, your, yours)</Option>
                            <Option value='THIRD_PERSON'>Third person (he, she, it, they)</Option>
                        </Select>
                    </Descriptions.Item>
                    <Descriptions.Item label='Content type'>
                        <Select value={this.state.content_type} onChange={(value) => this.setState({ content_type: value })} style={{width: '100%'}}>
                            <Option value='BLOG_POST'>Blog post</Option>
                            <Option value='LISTICLE'>Listicle (top N...)</Option>
                            <Option value='REVIEW'>Review</Option>
                            <Option value='COMPARISON_REVIEW'>Comparison review (A vs B)</Option>
                            <Option value='HOW_TO'>How-to/Tutorial</Option>
                            <Option value='GUIDE'>Guide</Option>
                            <Option value='EDITORIAL'>Editorial</Option>
                        </Select>
                    </Descriptions.Item>
                </Descriptions>
                <br />
                <Form layout='vertical'>
                    <Form.Item label='Article description:'>
                        <TextArea rows={6} value={this.state.description} onChange={(e) => this.setState({ description: e.target.value })} />
                    </Form.Item>
                    <Form.Item label='Target keywords:' extra={<em>Enter keywords separated by commas. These help shape the article’s focus and will be repeated throughout the content for consistency. (Optional)</em>}>
                        <TextArea rows={4} onChange={(e) => this.setState({ keywords: e.target.value })} />
                    </Form.Item>
                    <Form.Item label='Additional information:' extra={<em>Provide any additional context you would like us to consider. You can also include links to seed articles for inspiration. (Optional)</em>}>
                        <TextArea rows={4} onChange={(e) => this.setState({ additional_info: e.target.value })} />
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                centered
                title='Example Article Descriptions'
                open={this.state.showExampleRequestsModal}
                width={600}
                destroyOnClose={true}
                footer={false}
                onCancel={() => this.setState({ showExampleRequestsModal: false })}>

                <p>Below you can find examples of good article descriptions.</p>

                <Collapse accordion>
                    <Panel header="Example 1" key="1">
                        <p>Write an article about how regular walking improves cardiovascular health and mental well being.</p>
                    </Panel>
                    <Panel header="Example 2" key="2">
                        <p>Write an article listing 7 easy ways to reduce household waste and live more sustainably.</p>
                    </Panel>
                    <Panel header="Example 3" key="3">
                        <p>Write a article for small business owners about effective customer retention strategies in 2025.
                            Discuss personalization, loyalty programs, post-purchase engagement, and data driven insights.</p>
                    </Panel>
                    <Panel header="Example 4" key="4">
                        <p>Write a travel article titled "Top 10 Underrated Cities to Visit in Europe in 2025"
                            For each city, include a short paragraph covering unique attractions, local cuisine, cultural highlights, and best travel season.
                            End with a summary paragraph about off-the-beaten-path tourism trends.</p>
                    </Panel>
                    <Panel header="Example 5" key="5">
                        <p>Write an educational article for high school students on how renewable energy sources are changing the world.
                            Explain solar, wind, and hydroelectric power, discuss environmental benefits vs. challenges, and include real world examples of successful renewable projects.
                            Keep the tone clear, engaging, and informative.</p>
                    </Panel>
                    <Panel header="Example 6" key="6">
                        <p>Write an article explaining how coral reefs are affected by climate change.
                            Discuss rising ocean temperatures, acidification, bleaching events, and conservation efforts.</p>
                    </Panel>
                    <Panel header="Example 7" key="7">
                        <p>Write an article about common mistakes people make when budgeting and how to avoid them.</p>
                    </Panel>
                    <Panel header="Example 8" key="8">
                        <p>Write an article about the benefits of lifelong learning for career development.</p>
                    </Panel>
                    <Panel header="Example 9" key="9">
                        <p>Create an article discussing the impact of social media algorithms on political polarization.
                            Include examples of recommendation systems, echo chambers, and misinformation risks, and suggest possible policy or design solutions.</p>
                    </Panel>
                    <Panel header="Example 10" key="10">
                        <p>Write an article analyzing how artificial intelligence is transforming supply chain management in global manufacturing.
                            Include: predictive analytics for demand forecasting, automation in warehousing, supplier risk management, and ethical challenges in data use.
                            Use real-world company examples (like Siemens or Toyota) and finish with insights about future trends.</p>
                    </Panel>
                    <Panel header="Example 11" key="11">
                        <p>Write an in-depth article explaining how gut microbiome health influences mental well-being.
                            Cover: gut-brain axis, neurotransmitter production, probiotics vs. prebiotics, current scientific research, and practical dietary advice.
                            Use a balanced, evidence-based tone and include 2–3 citations to recent studies.</p>
                    </Panel>
                    <Panel header="Example 12" key="12">
                        <p>Write an article for college students on practical ways to live sustainably on campus.
                            Discuss: reducing waste, choosing eco-friendly transportation, sustainable eating habits, and organizing green student initiatives.
                            Keep the tone friendly, encouraging, and action-focused.</p>
                    </Panel>
                </Collapse>

            </Modal>
        </>;
    }
}

export default connect(null, { generateContentWriterArticle })(GenerateArticleModal);